import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, User, ArrowRight, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import Logo from "../components/Logo";
import { API, setToken } from "../lib/auth";

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, {
        username: username.trim(),
        password: password.trim(),
      });
      setToken(res.data.access_token);
      navigate("/admin");
    } catch (err) {
      toast({ title: "Login failed", description: "Incorrect username or password.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen navy-gradient flex items-center justify-center px-6 relative overflow-hidden">
      <div className="blob h-72 w-72 bg-blue-500 -top-10 left-10" />
      <div className="blob h-72 w-72 bg-sky-400 bottom-0 right-10" />
      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo light />
        </div>
        <div className="rounded-3xl bg-white shadow-2xl p-8 md:p-10">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl brand-gradient shadow-lg shadow-blue-600/25 mx-auto">
            <ShieldCheck className="h-7 w-7 text-white" />
          </div>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900 text-center">Leads Dashboard</h1>
          <p className="mt-1 text-slate-500 text-center text-sm">Sign in to manage leads and your blog.</p>
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin" className="pl-9" autoComplete="username" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="pl-9 pr-10" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" size="lg" disabled={loading} className="w-full brand-gradient text-white font-bold group">
              {loading ? "Signing in..." : "Sign In"}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
